import type { MouseEvent } from 'react';
import type { StageDetail, TimelineStageKey } from '@/api/cockpit';
import {
  StateMachineTimeline,
  TIMELINE_STAGES,
  type TimelineActiveStep,
  type TimelineSubstepSelection,
} from '@/components/common/StateMachineTimeline';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

export interface OrderProgressCardProps {
  currentStatus: string;
  currentStageKey: TimelineStageKey | null;
  currentStageIndex: number;
  stageDetails: Record<TimelineStageKey, StageDetail>;
  selectedStageKey: TimelineStageKey | null;
  selectedSubstep: TimelineSubstepSelection | null;
  activeTimelineSteps: readonly TimelineActiveStep[];
  isApprovalDraftWaiting: boolean;
  showSubsteps: boolean;
  onToggleSubsteps: () => void;
  onSelectStage: (key: TimelineStageKey) => void;
  onSelectSubstep: (stageKey: TimelineStageKey, substep: TimelineSubstepSelection) => void;
  onApprovalReopen: () => void;
  onStageContextMenu: (
    event: MouseEvent,
    stageKey: TimelineStageKey,
    substep?: TimelineSubstepSelection | null,
  ) => void;
}

export function OrderProgressCard(props: OrderProgressCardProps): JSX.Element {
  function isStageReachable(stageKey: TimelineStageKey): boolean {
    const index = TIMELINE_STAGES.findIndex((stage) => stage.key === stageKey);
    return index <= props.currentStageIndex;
  }
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 pb-3">
        <div className="space-y-1.5">
          <CardTitle className="text-base">Order Progress</CardTitle>
          <CardDescription>See where this order is in the process.</CardDescription>
        </div>
        <label className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
          <span>Show details</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={props.onToggleSubsteps}
            aria-label="Toggle progress details"
          >
            {props.showSubsteps ? 'On' : 'Off'}
          </Button>
        </label>
      </CardHeader>
      <CardContent className="overflow-visible">
        <StateMachineTimeline
          currentStatus={props.currentStatus}
          currentStageKey={props.currentStageKey}
          selectedStageKey={props.selectedStageKey}
          stageDetails={props.stageDetails}
          showSubsteps={props.showSubsteps}
          activeSteps={props.activeTimelineSteps}
          selectedSubstepId={
            props.selectedStageKey && props.selectedSubstep
              ? `${props.selectedStageKey}:${props.selectedSubstep.id}`
              : null
          }
          onStageClick={(stageKey) => {
            if (stageKey === 'approval' && props.isApprovalDraftWaiting) {
              props.onApprovalReopen();
              return;
            }
            if (isStageReachable(stageKey)) {
              props.onSelectStage(stageKey);
            }
          }}
          onStageContextMenu={(event, stageKey) => {
            if (isStageReachable(stageKey)) {
              props.onStageContextMenu(event, stageKey);
            }
          }}
          onSubstepClick={props.onSelectSubstep}
          onSubstepContextMenu={(event, stageKey, substep) => {
            props.onStageContextMenu(event, stageKey, substep);
          }}
        />
      </CardContent>
    </Card>
  );
}
